<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import dayjs from 'dayjs';
import gettext from '@/i18n/gettext';
import type { DevaugePalletizingStatus } from '@/models/DevaugePalletizingStatus';
import loadDevaugePalletizingStatus from '@/api/DevaugePalletizingApi';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();

const palletizingStatuses = ref<DevaugePalletizingStatus[]>([]);

const formatTimestamp = (timestamp: number) => {
  const date = dayjs(timestamp * 1000);
  return date.format('DD.MM.YYYY HH:mm:ss');
};

const fetchPalletizingStatuses = async () => {
  try {
    const data: DevaugePalletizingStatus[] =
      await loadDevaugePalletizingStatus('tangermunde');
    data.sort((a, b) => a.name.localeCompare(b.name));
    palletizingStatuses.value = data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error('Error fetching palletizing statuses:', err);
  }
};

let fetchInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  fetchPalletizingStatuses();
  loadingSpinnerStore.setShowLoadingOverlay(false);
  fetchInterval = setInterval(fetchPalletizingStatuses, 10000);
});

onUnmounted(() => {
  if (fetchInterval) {
    clearInterval(fetchInterval);
    fetchInterval = null;
  }
});
</script>

<template>
  <div class="pa-4 pa-sm-12">
    <v-row class="text-center">
      <v-col cols="12"> </v-col>
    </v-row>
    <v-row>
      <v-col class="mb-4">
        <h1 class="display-2 mb-3">
          {{ $gettext('Palletizing status') }}
        </h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        v-for="status in palletizingStatuses"
        :key="status.serialNumber"
        cols="12"
        sm="6"
        md="4"
        lg="4"
      >
        <v-card class="mx-auto" outlined>
          <v-card-title>
            <span>{{ status.name }}</span>
          </v-card-title>
          <v-card-subtitle>{{ status.serialNumber }}</v-card-subtitle>
          <v-card-text>
            <!-- Subcard for Right Pallet -->
            <v-card
              class="m-2"
              :class="
                status.palletStatusRight ? 'border-success' : 'border-warning'
              "
            >
              <v-card-title>
                {{ $gettext('Right Pallet') }}
              </v-card-title>
              <v-card-text>
                <v-icon
                  :class="
                    status.palletStatusRight ? 'text-success' : 'text-warning'
                  "
                >
                  mdi-package-variant
                </v-icon>
                <span
                  class="ml-2"
                  :class="
                    status.palletStatusRight ? 'text-success' : 'text-warning'
                  "
                >
                  {{
                    status.palletStatusRight
                      ? $gettext('Done')
                      : $gettext('In Progress')
                  }}
                </span>
              </v-card-text>
            </v-card>

            <!-- Subcard for Left Pallet -->
            <v-card
              class="mt-2"
              :class="
                status.palletStatusLeft ? 'border-success' : 'border-warning'
              "
            >
              <v-card-title>
                {{ $gettext('Left Pallet') }}
              </v-card-title>
              <v-card-text>
                <v-icon
                  :class="
                    status.palletStatusLeft ? 'text-success' : 'text-warning'
                  "
                >
                  mdi-package-variant
                </v-icon>
                <span
                  class="ml-2"
                  :class="
                    status.palletStatusLeft ? 'text-success' : 'text-warning'
                  "
                >
                  {{
                    status.palletStatusLeft
                      ? $gettext('Done')
                      : $gettext('In Progress')
                  }}
                </span>
              </v-card-text>
            </v-card>

            <!-- Common Timestamp -->
            <p class="mt-2">
              {{ $gettext('Last update') }}:
              {{ formatTimestamp(status.timestamp) }}
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
.border-success {
  border: 3px solid green;
}

.border-warning {
  border: 3px solid orange;
}
</style>
